import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, FileText, Loader2, ExternalLink, Cloud, CloudOff, ShieldCheck, Search,
  Filter, Trash2, Calendar as CalendarIcon, Receipt, Pencil, Save, X,
  ScanSearch, AlertCircle, Lock, Check, UploadCloud,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { usePinSecurity } from "@/contexts/PinContext";
import ExpenseNewDialog from "./ExpenseNewDialog";
import ManualCropDialog from "./ManualCropDialog";
import CropPreviewDialog from "./CropPreviewDialog";

// V1.36 · Panel Gastos — Lista con filtros + Nuevo gasto.
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1];
const QUARTERS = [1, 2, 3, 4];

export default function ExpensesPanel({ sectionId, sectionLabel }) {
  const pin = usePinSecurity();
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ base: 0, vat: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null); // V1.38.9 · gasto en edición (PIN)
  const [filters, setFilters] = useState({
    year: CURRENT_YEAR, quarter: "", main_account: "", supplier_id: "", q: "",
  });
  const [tree, setTree] = useState({});
  const [suppliers, setSuppliers] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null) params[k] = v; });
      if (sectionId) params.section_id = sectionId;
      const { data } = await api.get("/accounting/expenses", { params });
      setItems(data?.items || []);
      setTotals(data?.totals || { base: 0, vat: 0, total: 0 });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error");
    } finally {
      setLoading(false);
    }
  }, [filters, sectionId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const params = sectionId ? { section_id: sectionId } : {};
        const [{ data: a }, { data: s }] = await Promise.all([
          api.get("/accounting/accounts"),
          api.get("/accounting/suppliers", { params }),
        ]);
        setTree(a?.tree || {});
        setSuppliers(s?.items || []);
      } catch { /* silencioso */ }
    })();
  }, [sectionId]);

  const del = async (id, correlativo) => {
    try {
      await pin.require("accounting.expense.delete");
      if (!window.confirm(`¿Eliminar el gasto #${correlativo}? Esta acción se audita.`)) return;
      await api.delete(`/accounting/expenses/${id}`);
      toast.success(`Gasto #${correlativo} eliminado`);
      load();
    } catch (err) {
      const m = formatApiError(err?.response?.data?.detail) || err?.message;
      if (m && !/cancel/i.test(m)) toast.error(m);
    }
  };

  const openPdf = (item) => {
    // Preferimos OneDrive; si no, endpoint que sirve desde Mongo
    const url = item.onedrive?.web_url;
    if (url) window.open(url, "_blank");
    else {
      const base = process.env.REACT_APP_BACKEND_URL;
      window.open(`${base}/api/accounting/expenses/${item.id}/pdf`, "_blank");
    }
  };

  // V1.40 · Verificar / re-sync OneDrive · borra local si sube OK
  const [verifyingId, setVerifyingId] = useState("");
  const verifyOneDrive = async (item) => {
    setVerifyingId(item.id);
    try {
      const { data } = await api.post(
        `/accounting/expenses/${item.id}/verify-onedrive`,
      );
      if (data.verified) {
        toast.success(
          `✅ #${item.correlativo} verificado en OneDrive · copia local eliminada`,
        );
      } else if (data.reason === "fake_provider") {
        toast.info(
          "OneDrive no configurado en este entorno (preview). En producción la verificación borra la copia local.",
          { duration: 5000 },
        );
      } else {
        toast.warning(
          `⚠ #${item.correlativo} aún NO está en OneDrive (${data.reason || "pendiente"})`,
        );
      }
      load();
    } catch (err) {
      const m = formatApiError(err?.response?.data?.detail) || err?.message || "Error";
      toast.error(m);
    } finally {
      setVerifyingId("");
    }
  };

  return (
    <div className="space-y-3" data-testid="expenses-panel">
      {/* Filtros + botón nuevo */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <input
            placeholder="Buscar factura / proveedor / notas…"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-neutral-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            data-testid="expenses-search"
          />
        </div>
        <select value={filters.year}
                onChange={(e) => setFilters({ ...filters, year: Number(e.target.value) })}
                className={selCls} data-testid="expenses-filter-year">
          <option value="">Año</option>
          {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={filters.quarter}
                onChange={(e) => setFilters({ ...filters, quarter: e.target.value })}
                className={selCls} data-testid="expenses-filter-quarter">
          <option value="">Trim.</option>
          {QUARTERS.map((q) => <option key={q} value={q}>T{q}</option>)}
        </select>
        <select value={filters.main_account}
                onChange={(e) => setFilters({ ...filters, main_account: e.target.value })}
                className={selCls} data-testid="expenses-filter-account">
          <option value="">Cuenta</option>
          {Object.keys(tree).sort().map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={filters.supplier_id}
                onChange={(e) => setFilters({ ...filters, supplier_id: e.target.value })}
                className={selCls} data-testid="expenses-filter-supplier">
          <option value="">Proveedor</option>
          {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <button
          onClick={() => setOpenNew(true)}
          className="btn-primary !h-10 !px-4 !text-sm"
          data-testid="expenses-new-btn"
        >
          <Plus className="h-4 w-4" /> Nuevo gasto
        </button>
      </div>

      {/* Totales header */}
      <div className="grid grid-cols-3 gap-2">
        <StatCard label="Base imponible" value={totals.base} />
        <StatCard label="IVA" value={totals.vat} />
        <StatCard label="Total" value={totals.total} highlight />
      </div>

      {/* Lista */}
      <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-neutral-500">
            <Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Cargando…
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <Receipt className="mx-auto h-8 w-8 text-neutral-300 mb-2" />
            <div className="text-sm text-neutral-500">
              No hay gastos con estos filtros. Empieza registrando uno con
              <b className="text-orange-600"> + Nuevo gasto</b>.
            </div>
          </div>
        ) : (
          <table className="w-full text-sm table-fixed">
            <colgroup>
              <col style={{ width: "68px" }} />
              <col style={{ width: "110px" }} />
              <col />
              <col style={{ width: "140px" }} />
              <col style={{ width: "220px" }} />
              <col style={{ width: "100px" }} />
              <col style={{ width: "90px" }} />
              <col style={{ width: "110px" }} />
              <col style={{ width: "160px" }} />
            </colgroup>
            <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-4 py-3">#</th>
                <th className="text-left px-4 py-3">Fecha</th>
                <th className="text-left px-4 py-3">Proveedor</th>
                <th className="text-left px-4 py-3">Factura</th>
                <th className="text-left px-4 py-3">Cuenta / Tipo</th>
                <th className="text-right px-4 py-3">Base</th>
                <th className="text-right px-4 py-3">IVA</th>
                <th className="text-right px-4 py-3">Total</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="border-t border-neutral-100 hover:bg-neutral-50/60 h-[56px]"
                    data-testid={`expense-row-${e.id}`}>
                  <td className="px-4 py-3 font-bold tabular-nums align-middle">#{e.correlativo}</td>
                  <td className="px-4 py-3 text-neutral-600 tabular-nums text-xs align-middle whitespace-nowrap">
                    {e.invoice_date || "—"}
                  </td>
                  <td className="px-4 py-3 align-middle">
                    <div className="font-medium truncate" title={e.supplier_name}>{e.supplier_name}</div>
                    {e.supplier_cif && <div className="text-[10px] text-neutral-500 truncate">{e.supplier_cif}</div>}
                  </td>
                  <td className="px-4 py-3 text-neutral-600 text-xs align-middle truncate" title={e.invoice_number || ""}>{e.invoice_number || "—"}</td>
                  <td className="px-4 py-3 text-neutral-600 text-xs align-middle truncate" title={`${e.main_account} · ${e.expense_kind}`}>
                    <b className="text-neutral-800">{e.main_account}</b> · {e.expense_kind}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums align-middle">{fmt(e.base_amount)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-neutral-500 align-middle">{fmt(e.vat_amount)}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-bold align-middle">{fmt(e.total_amount)}</td>
                  <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap align-middle">
                    <button onClick={() => openPdf(e)}
                            title="Ver PDF"
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded hover:bg-neutral-100"
                            data-testid={`expense-pdf-${e.id}`}>
                      <FileText className="h-3.5 w-3.5" />
                    </button>
                    {e.onedrive?.web_url && (
                      <a href={e.onedrive.web_url} target="_blank" rel="noreferrer"
                         title="Abrir en OneDrive"
                         className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded hover:bg-neutral-100 text-sky-600"
                         data-testid={`expense-onedrive-${e.id}`}>
                        <Cloud className="h-3.5 w-3.5" />
                      </a>
                    )}
                    {(e.sync_pending || !e.onedrive?.uploaded) && (
                      <button onClick={() => verifyOneDrive(e)}
                              disabled={verifyingId === e.id}
                              title="Verificar/reenviar a OneDrive · borra copia local"
                              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded hover:bg-amber-50 text-amber-700 disabled:opacity-60"
                              data-testid={`expense-verify-onedrive-${e.id}`}>
                        {verifyingId === e.id
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <ShieldCheck className="h-3.5 w-3.5" />}
                      </button>
                    )}
                    <button onClick={() => setEditing(e)}
                            title="Editar importes (requiere PIN Maestro)"
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded hover:bg-blue-50 text-blue-700"
                            data-testid={`expense-edit-${e.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => del(e.id, e.correlativo)}
                            title="Eliminar (requiere PIN)"
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded hover:bg-red-50 text-red-600"
                            data-testid={`expense-del-${e.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ExpenseNewDialog
        open={openNew}
        sectionId={sectionId}
        sectionLabel={sectionLabel}
        onClose={() => setOpenNew(false)}
        onCreated={() => load()}
      />

      {editing && (
        <ExpenseAmountEditor
          expense={editing}
          pin={pin}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// V1.38.9 · Mini-modal para editar importes de un gasto ya guardado.
// V1.41 · Ampliado con proveedor + CIF, guardia MATRIZ, comparación
// OCR vs actual y botón "Reprocesar OCR" (solo matriz).
// El PATCH del backend exige PIN Maestro; los campos sensibles
// (fiscales/proveedor/CIF) solo son editables por la MATRIZ.
function ExpenseAmountEditor({ expense, pin, onClose, onSaved }) {
  const { user } = useAuth();
  // V1.42.7 · Usamos el flag `is_matrix` calculado en backend (compatible con
  // ENV `MATRIX_EMAIL`). Fallback al hardcode legacy si el token viejo no
  // trae el campo (usuarios ya logueados antes del deploy).
  const isMatrix = user?.is_matrix === true
    || ((user?.email || "").toLowerCase().trim() === "naturautoescuela@gmail.com");
  const [validated, setValidated] = useState(!!expense.validated);
  const [validating, setValidating] = useState(false);
  const [cropOpen, setCropOpen] = useState(false); // V1.42.2 · ManualCropDialog
  const [replacingOd, setReplacingOd] = useState(false); // V1.42.4 · sustituir original en OneDrive
  const [previewOpen, setPreviewOpen] = useState(false); // V1.42.8 · preview antes de sustituir
  const locked = validated && !isMatrix;
  const [form, setForm] = useState({
    supplier_name: expense.supplier_name || "",
    supplier_cif: expense.supplier_cif || "",
    invoice_number: expense.invoice_number || "",
    invoice_date: expense.invoice_date || "",
    base_amount: expense.base_amount ?? 0,
    vat_percent: expense.vat_percent ?? 0,
    vat_amount: expense.vat_amount ?? 0,
    total_amount: expense.total_amount ?? 0,
    notes: expense.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [snapLoading, setSnapLoading] = useState(true);
  const [reocring, setReocring] = useState(false);
  const [showCompare, setShowCompare] = useState(false);

  // Cargar comparación OCR al abrir
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/accounting/expenses/${expense.id}/ocr-snapshot`);
        if (alive) setSnapshot(data);
      } catch (_) { /* silencio */ }
      finally { if (alive) setSnapLoading(false); }
    })();
    return () => { alive = false; };
  }, [expense.id]);

  // Auto-recalcula IVA + total al cambiar base o %
  useEffect(() => {
    const base = Number(form.base_amount) || 0;
    const pct = Number(form.vat_percent) || 0;
    const vat = Math.round((base * pct / 100) * 100) / 100;
    const total = Math.round((base + vat) * 100) / 100;
    setForm((f) => ({ ...f, vat_amount: vat, total_amount: total }));
  }, [form.base_amount, form.vat_percent]);

  const save = async () => {
    setSaving(true);
    try {
      await pin.require("accounting.expense.edit");
      const { data } = await api.patch(`/accounting/expenses/${expense.id}`, form);
      const sens = data?.sensitive_changed || [];
      if (sens.length) {
        toast.success(`Gasto actualizado · MATRIZ modificó: ${sens.join(", ")}`);
      } else {
        toast.success("Gasto actualizado");
      }
      onSaved?.();
    } catch (err) {
      const m = formatApiError(err?.response?.data?.detail) || err?.message;
      if (m && !/cancel/i.test(m)) toast.error(m, { duration: 6000 });
    } finally { setSaving(false); }
  };

  const reocr = async () => {
    if (!window.confirm("¿Reprocesar OCR del PDF? Actualiza el snapshot pero NO modifica los valores actuales.")) return;
    setReocring(true);
    try {
      await api.post(`/accounting/expenses/${expense.id}/reocr`);
      toast.success("OCR reprocesado · snapshot actualizado");
      // Recarga el snapshot
      const { data } = await api.get(`/accounting/expenses/${expense.id}/ocr-snapshot`);
      setSnapshot(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err?.message || "Error");
    } finally { setReocring(false); }
  };

  // Estilo para input sensible bloqueado a no-matriz
  const sensCls = isMatrix
    ? "w-full h-10 rounded-lg border border-neutral-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
    : "w-full h-10 rounded-lg border border-neutral-300 bg-neutral-100 text-neutral-500 px-3 text-sm cursor-not-allowed";

  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" onClick={onClose}
         data-testid="expense-editor-dialog">
      <div className="bg-white rounded-2xl w-full max-w-2xl p-5 shadow-2xl max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-bold text-lg">Editar gasto #{expense.correlativo}</h3>
            <div className="text-xs text-neutral-500">{expense.supplier_name}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-neutral-100"><X className="h-4 w-4" /></button>
        </div>

        {/* Guardia visual MATRIZ */}
        {isMatrix ? (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-[11px] text-emerald-800 mb-3">
            <b>MATRIZ.</b> Puedes modificar todos los campos, incluidos los fiscales. Los cambios quedan auditados con diff antes/después.
          </div>
        ) : (
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800 mb-3">
            <b>Acceso limitado.</b> Los campos fiscales (proveedor, CIF, factura, fecha e importes) solo puede modificarlos la MATRIZ. Puedes editar notas y guardar con PIN Maestro.
          </div>
        )}

        {/* Snapshot OCR banner */}
        {!snapLoading && snapshot && (
          <div className="rounded-lg bg-neutral-50 border border-neutral-200 px-3 py-2 mb-3 flex items-center gap-2">
            {snapshot.has_snapshot ? (
              <>
                <ScanSearch className="h-4 w-4 text-blue-600" />
                <span className="text-[11px] text-neutral-700">
                  Snapshot OCR disponible {snapshot.diff && Object.keys(snapshot.diff).length > 0 &&
                    <b className="text-orange-700"> · {Object.keys(snapshot.diff).length} campo(s) modificados manualmente</b>}
                </span>
                <button onClick={() => setShowCompare((v) => !v)}
                        className="ml-auto text-[10px] font-semibold text-blue-700 hover:underline"
                        data-testid="expense-toggle-compare">
                  {showCompare ? "Ocultar comparación" : "Ver comparación"}
                </button>
              </>
            ) : (
              <>
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <span className="text-[11px] text-amber-800">
                  <b>Sin snapshot OCR</b> (gasto anterior a V1.41).
                </span>
                {isMatrix && (
                  <button onClick={reocr} disabled={reocring}
                          className="ml-auto text-[10px] font-semibold text-emerald-700 hover:underline disabled:opacity-60"
                          data-testid="expense-reocr-btn">
                    {reocring ? "Reprocesando..." : "Reprocesar OCR"}
                  </button>
                )}
              </>
            )}
            {snapshot.has_snapshot && isMatrix && (
              <button onClick={reocr} disabled={reocring}
                      className="text-[10px] font-semibold text-emerald-700 hover:underline disabled:opacity-60 ml-2"
                      data-testid="expense-reocr-btn-2">
                {reocring ? "..." : "Re-OCR"}
              </button>
            )}
          </div>
        )}

        {/* Vista comparación */}
        {showCompare && snapshot?.has_snapshot && (
          <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 mb-3" data-testid="expense-ocr-comparison">
            <div className="text-[11px] font-bold text-blue-800 mb-2">
              OCR original vs Valor actual
            </div>
            <table className="w-full text-[10px]">
              <thead>
                <tr className="text-neutral-500 border-b border-blue-200">
                  <th className="text-left py-1">Campo</th>
                  <th className="text-left py-1">OCR</th>
                  <th className="text-left py-1">Actual</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(snapshot.ocr || {}).map(([k, v]) => {
                  const cur = snapshot.current?.[k];
                  const changed = v !== cur;
                  return (
                    <tr key={k} className={changed ? "bg-orange-50" : ""}>
                      <td className="py-1 font-semibold text-neutral-700">{k}</td>
                      <td className="py-1 text-neutral-500">{String(v || "—")}</td>
                      <td className={"py-1 " + (changed ? "text-orange-700 font-semibold" : "text-neutral-600")}>{String(cur || "—")}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-neutral-600 block mb-1">Proveedor {!isMatrix && <Lock className="inline h-3 w-3 ml-1 text-amber-600" />}</label>
              <input value={form.supplier_name}
                     onChange={(e) => setForm({ ...form, supplier_name: e.target.value })}
                     readOnly={!isMatrix}
                     data-testid="edit-supplier-name"
                     className={sensCls} />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-600 block mb-1">CIF {!isMatrix && <Lock className="inline h-3 w-3 ml-1 text-amber-600" />}</label>
              <input value={form.supplier_cif}
                     onChange={(e) => setForm({ ...form, supplier_cif: e.target.value.toUpperCase() })}
                     readOnly={!isMatrix}
                     data-testid="edit-supplier-cif"
                     className={sensCls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-neutral-600 block mb-1">Nº Factura {!isMatrix && <Lock className="inline h-3 w-3 ml-1 text-amber-600" />}</label>
              <input value={form.invoice_number}
                     onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                     readOnly={!isMatrix}
                     data-testid="edit-invoice-number"
                     className={sensCls} />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-600 block mb-1">Fecha {!isMatrix && <Lock className="inline h-3 w-3 ml-1 text-amber-600" />}</label>
              <input type="date" value={form.invoice_date}
                     onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
                     readOnly={!isMatrix}
                     data-testid="edit-invoice-date"
                     className={sensCls} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold text-neutral-600 block mb-1">Base (€) {!isMatrix && <Lock className="inline h-3 w-3 ml-1 text-amber-600" />}</label>
              <input type="number" step="0.01" value={form.base_amount}
                     onChange={(e) => setForm({ ...form, base_amount: e.target.value })}
                     readOnly={!isMatrix}
                     data-testid="edit-base-amount"
                     className={sensCls + " tabular-nums text-right"} />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-600 block mb-1">IVA % {!isMatrix && <Lock className="inline h-3 w-3 ml-1 text-amber-600" />}</label>
              <input type="number" step="1" value={form.vat_percent}
                     onChange={(e) => setForm({ ...form, vat_percent: e.target.value })}
                     readOnly={!isMatrix}
                     data-testid="edit-vat-percent"
                     className={sensCls + " tabular-nums text-right"} />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-600 block mb-1">Total (€) {!isMatrix && <Lock className="inline h-3 w-3 ml-1 text-amber-600" />}</label>
              <input type="number" step="0.01" value={form.total_amount}
                     onChange={(e) => setForm({ ...form, total_amount: e.target.value })}
                     readOnly={!isMatrix}
                     data-testid="edit-total-amount"
                     className={sensCls + " tabular-nums text-right font-semibold"} />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-600 block mb-1">Notas</label>
            <textarea value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      rows={2} data-testid="edit-notes"
                      className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-5">
          {/* V1.42 · Botón VALIDADO / DESBLOQUEAR */}
          {!validated ? (
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm("¿Marcar esta factura como VALIDADA?\nQuedará bloqueada para edición (solo matriz podrá desbloquearla).")) return;
                setValidating(true);
                try {
                  await pin.require("accounting.expense.validate");
                  await api.post(`/accounting/expenses/${expense.id}/validate`);
                  setValidated(true);
                  toast.success("Factura validada y bloqueada");
                  onSaved?.();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "No se pudo validar");
                } finally { setValidating(false); }
              }}
              disabled={validating || saving}
              className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-60"
              data-testid="expense-validate-btn"
            >
              {validating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Marcar validada
            </button>
          ) : isMatrix ? (
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm("¿Desbloquear esta factura validada?")) return;
                setValidating(true);
                try {
                  await pin.require("accounting.expense.unvalidate");
                  await api.post(`/accounting/expenses/${expense.id}/unvalidate`);
                  setValidated(false);
                  toast.success("Factura desbloqueada");
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "No se pudo desbloquear");
                } finally { setValidating(false); }
              }}
              className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-900 text-xs font-semibold"
              data-testid="expense-unvalidate-btn"
            >
              Desbloquear
            </button>
          ) : (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1" data-testid="expense-validated-badge">
              <Check className="h-3 w-3" /> Validada
            </span>
          )}
          <button onClick={onClose} className="btn-secondary !h-9 !px-3 !text-xs">Cancelar</button>
          {/* V1.42.2 · Recorte manual reutilizando ManualCropDialog (mismo
              flujo que DNI). No modifica la imagen original: sólo guarda
              `crop_config` como metadato. Bloqueado si Validada y no matriz. */}
          <button
            type="button"
            onClick={() => setCropOpen(true)}
            disabled={locked}
            className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-neutral-300 hover:bg-neutral-50 text-xs font-semibold disabled:opacity-50"
            data-testid="expense-crop-btn"
            title={locked ? "Factura validada · bloqueada" : "Ajustar recorte / rotación / filtro"}
          >
            <ScanSearch className="h-4 w-4" /> CROP
          </button>
          {/* V1.42.4 · Sustituir el original en OneDrive por la versión
              recortada. Sólo matriz + factura validada. Al pulsar se abre
              una vista previa con la imagen final (V1.42.8) y allí se
              confirma. Al ejecutarse limpia `crop_config`. */}
          {validated && isMatrix && (
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              disabled={replacingOd || saving}
              className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold disabled:opacity-60"
              data-testid="expense-replace-onedrive-btn"
              title="Sustituir el archivo en OneDrive por la versión recortada (con vista previa)"
            >
              {replacingOd ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
              Sustituir original en OneDrive
            </button>
          )}
          <button onClick={save} disabled={saving || locked} className="btn-primary !h-9 !px-4 !text-xs" data-testid="edit-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar (PIN)
          </button>
        </div>
        {/* V1.42.8 · Vista previa antes de sustituir en OneDrive */}
        <CropPreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          title={`Vista previa · ${expense.pdf_filename || `expense-${expense.correlativo || expense.id}.pdf`}`}
          previewUrl={`/accounting/expenses/${expense.id}/preview-crop`}
          hasCrop={!!(expense.crop_config && expense.crop_config.mode === "manual")}
          onConfirm={async () => {
            setReplacingOd(true);
            try {
              await pin.require("accounting.expense.replace_original");
              const { data } = await api.post(`/accounting/expenses/${expense.id}/apply-crop-to-onedrive`);
              toast.success(data?.applied_crop
                ? "OneDrive actualizado con la versión recortada"
                : "OneDrive re-subido (sin recorte)");
              onSaved?.();
              onClose?.();
            } finally { setReplacingOd(false); }
          }}
        />
        {/* Diálogo de recorte manual — reutiliza el componente de DNI */}
        <ManualCropDialog
          open={cropOpen}
          apiBase={`/accounting/expenses/${expense.id}`}
          filename={expense.pdf_filename || `expense-${expense.correlativo || expense.id}.pdf`}
          initialConfig={expense.crop_config || null}
          onClose={() => setCropOpen(false)}
          onSaved={() => { setCropOpen(false); onSaved?.(); }}
        />
      </div>
    </div>
  );
}

const selCls =
  "h-10 rounded-lg border border-neutral-300 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500";

function fmt(v) {
  return `${Number(v || 0).toFixed(2)} €`;
}

function StatCard({ label, value, highlight }) {
  return (
    <div className={
      "rounded-xl border p-3 " +
      (highlight ? "border-orange-200 bg-orange-50" : "border-neutral-200 bg-white")
    }>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold">{label}</div>
      <div className={"text-lg font-bold tabular-nums " + (highlight ? "text-orange-700" : "text-neutral-900")}>
        {fmt(value)}
      </div>
    </div>
  );
}

// silence lint (icons imported for symmetry, may be used later)
void ExternalLink; void Filter; void CalendarIcon;
